import { Component, ViewChild, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ProcessListComponent } from '../../components/process-list/process-list.component';
import { ProcessFormComponent } from '../../components/process-form/process-form.component';
import { ProcessService } from '../../services/process.service';
import { PageHeaderComponent } from 'app/core/components/page-header/page-header.component';
import { MobileFooterComponent } from 'app/core/components/mobile-footer/mobile-footer.component';
import { AppSettings } from '@services/settings/app-settings.service';
import { FormMode } from 'app/core/constants/form-mode';
import { PermissionService } from '@services/core/permission.service';
import Swal from 'sweetalert2';
import { NgbModal, NgbModalRef, NgbModalModule } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-process-management',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ProcessListComponent,
    ProcessFormComponent,
    PageHeaderComponent,
    MobileFooterComponent,
    NgbModalModule
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './process-management.component.html',
  styleUrl: './process-management.component.scss'
})
export class ProcessManagementComponent {
  @ViewChild(ProcessListComponent) processList!: ProcessListComponent;
  @ViewChild('processModal') processModal: any;

  private modalRef?: NgbModalRef;
  selectedProcess: any = null;
  processForm: FormGroup;
  mode = signal<FormMode>(FormMode.VIEW);
  formMode = FormMode;
  showDetail = false;

  public appSettings = inject(AppSettings);
  readonly perm = inject(PermissionService);
  private fb = inject(FormBuilder);
  private processService = inject(ProcessService);
  private modalService = inject(NgbModal);

  public Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true
  });

  constructor() {
    this.processForm = this.fb.group({
      uuid: [null],
      slug: [null, [Validators.required, Validators.pattern(/^[A-Z][A-Z0-9_]*$/)]],
      name: [null, [Validators.required]],
      abbr: [null],
      category: ['CLAIM', [Validators.required]],
      settlementTarget: ['NONE', [Validators.required]],
      ruleMap: [[]],
      icon: ['fas fa-tasks'],
      requiresDocuments: [false],
      usesBpm: [false],
      groupSession: [false]
    });
  }

  get showMobileDetail(): boolean {
    return this.appSettings.isMobile() && this.showDetail;
  }

  onProcessSelected(process: any): void {
    this.openEditor(process, FormMode.EDIT);
  }

  private handleSuccess(msg: string): void {
    this.Toast.fire({ icon: 'success', title: msg });
    this.processList?.loadProcesses();
    this.mode.set(FormMode.VIEW);
    this.closeDetail();
  }

  // Extrae y normaliza el payload del formulario
  private payloadFromForm(): any {
    const raw = this.processForm.getRawValue();
    const ruleMap = Array.isArray(raw.ruleMap)
      ? raw.ruleMap.map((s: string) => String(s || '').trim().toUpperCase()).filter(Boolean)
      : [];
    return {
      slug: String(raw.slug ?? '').trim().toUpperCase(),
      name: String(raw.name ?? '').trim(),
      abbr: raw.abbr ? String(raw.abbr).trim().toUpperCase() : null,
      category: String(raw.category ?? 'CLAIM').toUpperCase(),
      settlementTarget: String(raw.settlementTarget ?? 'NONE').toUpperCase(),
      ruleMap,
      icon: raw.icon ? String(raw.icon).trim() : 'fas fa-tasks',
      requiresDocuments: !!raw.requiresDocuments,
      usesBpm: !!raw.usesBpm,
      groupSession: !!raw.groupSession
    };
  }

  // Guarda o actualiza segun el modo del formulario
  saveProcess(): void {
    if (this.processForm.invalid) {
      this.processForm.markAllAsTouched();
      return;
    }
    const data = this.payloadFromForm();
    if (this.mode() === FormMode.EDIT) {
      this.executeUpdate(data);
    } else {
      this.executeCreate(data);
    }
  }

  // Ejecuta la actualizacion de un proceso existente por UUID
  private executeUpdate(data: any): void {
    const targetUuid = this.selectedProcess?.uuid;
    if (!targetUuid) return;
    this.processService.update(targetUuid, data).subscribe({
      next: () => this.handleSuccess('Proceso actualizado correctamente'),
      error: (err) => this.showError(err, 'No se pudo actualizar el proceso')
    });
  }

  // Ejecuta la creacion de un nuevo proceso
  private executeCreate(data: any): void {
    this.processService.create(data).subscribe({
      next: () => this.handleSuccess('Proceso creado correctamente'),
      error: (err) => this.showError(err, 'No se pudo crear el proceso')
    });
  }

  // Muestra notificacion de error
  private showError(err: any, fallback: string): void {
    const title = err?.error?.message ?? err?.error?.error ?? fallback;
    this.Toast.fire({ icon: 'error', title });
  }

  // Abre ventana modal
  open(content: any, size: 'md' | 'lg' | 'xl' = 'lg'): void {
    this.modalRef = this.modalService.open(content, { size });
  }

  // Cierra ventana modal
  close(): void {
    this.modalRef?.close();
  }

  closeDetail(): void {
    this.showDetail = false;
    this.selectedProcess = null;
    this.mode.set(FormMode.VIEW);
    this.close();
  }

  newProcessModal(): void {
    this.openEditor(null, FormMode.CREATE);
  }

  private openEditor(process: any | null, mode: FormMode): void {
    this.selectedProcess = process;
    this.mode.set(mode);
    if (mode === FormMode.CREATE) {
      this.processForm.reset({
        uuid: null, slug: null, name: null, abbr: null,
        category: 'CLAIM', settlementTarget: 'NONE', ruleMap: [], icon: 'fas fa-tasks',
        requiresDocuments: false, usesBpm: false, groupSession: false
      });
      this.processForm.get('slug')?.enable();
    } else {
      this.processForm.reset();
      this.processForm.patchValue({
        uuid: process.uuid,
        slug: process.slug,
        name: process.name,
        abbr: process.abbr,
        category: process.category ?? 'CLAIM',
        settlementTarget: process.settlementTarget ?? 'NONE',
        ruleMap: Array.isArray(process.ruleMap) ? process.ruleMap : [],
        icon: process.icon ?? 'fas fa-tasks',
        requiresDocuments: process.requiresDocuments === true,
        usesBpm: process.usesBpm === true,
        groupSession: process.groupSession === true
      });
      this.processForm.get('slug')?.disable();
    }
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    if (this.appSettings.isMobile()) {
      this.showDetail = true;
      return;
    }
    this.open(this.processModal);
  }
}
